(ns graph-ui.web.dock-panes
  "WHICH RIGHT-DOCK PANES ARE SHOWING, and the two icons that say so.

  ;; @feature F-gi-v3-010-01-graph-ui-poc
  ;; @adr     ADR-graph-ui-v0-026-01-dockview-for-the-pattern-level
  ;; @spec    SPEC-alatus-v0-002-01-ikc1-functional-target

  ikc1's RULE, COPIED. packages/athens/src/App.tsx:

      const RIGHT_DOCK_VISIBILITY_KEY = 'athens:right-dock-visibility';
      … ?? { showGraph: true, showInternals: true, showSecondPage: false }
      const toggleInternals = useCallback(() =>
        setDockVisibility(prev => ({ ...prev, showInternals: !prev.showInternals })));

  Three facts follow from those lines and this namespace holds all three:
  two INDEPENDENT booleans, BOTH TRUE by default, kept in localStorage.

  George, 2026-09-05: \"the ikc1 is good enough to follow its features, no need
  to go fractal.\" So this is not a session slot, not a command and not a source.
  It is a browser preference, which is what ikc1 made it, and it therefore needs
  no bus restart to work.

  ── WHY A NAMESPACE OF ITS OWN ──────────────────────────────────────────────
  Two files need it and neither may require the other. graph-ui.web.widgets
  draws the dock; graph-ui.web.core draws the top bar, outside the widget tree.
  A shared atom in either would make a cycle.

  ── ONE DIFFERENCE FROM ikc1, AND IT IS FORCED ──────────────────────────────
  ikc1 was one application with one dock. ikc3 has islands, and only reader-v3
  has these panes. So a dock REGISTERS the pane ids it knows, and an icon for a
  pane no island is drawing stays disabled rather than doing nothing when
  pressed. That is this project's own convention for an affordance that is not
  available (CONCEPT-graph-ui-v0-010-01)."
  (:require [reagent.core :as r]))

(def ^:private storage-key
  "ikc1's own key, with this platform's prefix. The two products do not share a
   browser origin, so the name is for a person reading devtools."
  "ikc3:right-dock-visibility")

(def icon-panes
  "The panes the TOP BAR has an icon for, by the :pane :id the pattern gives
   them. Three, each with its own hand-drawn glyph and its own words in
   graph-ui.web.core.

   THE NAVIGATOR IS ONE OF THEM, since 2026-09-05. George: \"it should have
   collapse beside width control\". ikc1 had both on that edge — AppShell.tsx
   holds `isSidebarCollapsed` with a `toggleSidebar`, AND a drag, and dragging
   the divider un-collapses it. reader-v2's :split-pane kept the pair as a
   chevron beside the divider. reader-v3's dock had the drag and had lost the
   fold.

   NOT THE READING AREA. Hiding the thing you are reading behind a toolbar icon
   is not something ikc1 offered and not something to invent here.

   THIS IS NO LONGER WHAT `can fold` MEANS (2026-09-09). It used to be: this set
   was the only implementation of a fact the patterns had declared since they
   were written, and the research pattern declares `can fold` on SEVEN panes.
   Foldability now comes from the pane itself, through :foldable, and this set
   says only which three have a bespoke icon."
  ;; THE DRAWER OF THE SUPERVISOR joined 2026-10-14 (IKC3/PLAN-ikc-v3-067-01/SV-2).
  ;; Each tracker island holds it as the pane supervisor-pane, and the top bar
  ;; button Supervisor opens and folds it (George, D6: "The drawer").
  #{"navigator" "graph-view" "frame-internals" "supervisor-pane"})

(defn- read-stored
  "The stored map, or nil. A private window, cleared site data or a browser that
   refuses storage all answer nil, and nil means the default."
  []
  (try
    (when-let [raw (some-> js/localStorage (.getItem storage-key))]
      (let [m (js->clj (js/JSON.parse raw) :keywordize-keys true)]
        (when (map? m) m)))
    (catch :default _ nil)))

(defonce ^{:doc "THE PERSON'S OWN CHOICES ONLY, and nothing else — a map from a
  pane id to true or false, kept in localStorage.

  IT USED TO PRE-SEED EVERY ICON PANE AS TRUE, and that made a declared default
  unreachable (2026-09-09). `[graph-view is folded]` seeded nothing, because
  register-panes! only filled an id this map had no answer for and this map
  already answered `true` for all three icon panes. The island opened with
  eight panes and George said so: \"I still see the old island look.\"

  SO THE DEFAULT IS DECIDED AT READ TIME, in showing?, from the pane's own
  declaration. An ABSENT entry here means the person has never chosen, which is
  a different fact from a stored false and must stay different.

  The earlier fault this docstring recorded is still worth keeping: `navigator`
  once joined the icon set without joining a second list, `showing?` read nil,
  and the sidebar opened COLLAPSED. The answer then was to seed both from one
  list. The answer now is to have no second list at all."}
  showing
  (r/atom (or (read-stored) {})))

;; WHICH PANE IDS THE DRAWN DOCK ACTUALLY HAS. Written by widget-dock on every
;; render, read by the top bar to decide whether an icon is live. An island with
;; no graph pane gets a disabled icon rather than a dead one.
(defonce known (r/atom #{}))

;; WHAT EACH DRAWN PANE DECLARED about folding: {id {:foldable bool :folded bool}}.
;; Written by widget-dock on every render, beside `known`.
(defonce declared-folds (r/atom {}))

(defn foldable?
  "May a person fold this pane? The PANE says so, through `[x can fold]` in the
   pattern — not a list in this namespace (2026-09-09). A pane that declared
   nothing is not foldable, which is every pane before the fact was read."
  [id]
  (boolean (get-in @declared-folds [id :foldable])))

(defn register-panes!
  "Told by the dock which panes it is drawing, and what each one declared about
   folding.

   IT WRITES NO DEFAULT. `:folded true` means the island opens with the pane
   closed — George, 2026-09-09: the graph view is not usable in its
   bare-minimum shape and can stay off by default, and so can the internals —
   but that answer is given by showing?, at READ time, from what is recorded
   here. This function runs on EVERY render, so anything it wrote into `showing`
   would fight a person who had just toggled a pane."
  [panes]
  (let [panes (for [p panes] (if (map? p) p {:id p}))
        ids   (set (keep :id panes))]
    (reset! known ids)
    (reset! declared-folds
            (into {} (for [{:keys [id foldable folded]} panes :when id]
                       [id {:foldable (boolean foldable) :folded (boolean folded)}])))
    nil))

(defn showing?
  "Is this pane showing?

   THREE ANSWERS, IN THIS ORDER.

   1. A pane that cannot fold is always showing. The question is about what a
      person may CLOSE, not about what a dock may draw.
   2. What the PERSON chose, if they ever have — a stored value or a toggle in
      this visit. Their answer beats every declaration, always.
   3. Otherwise the PANE's own declaration: `[x is folded]` opens it closed.

   An absent entry in `showing` is order 3 and not a `false`. Conflating the two
   is what made the declared default unreachable for an afternoon."
  [id]
  (cond
    (not (or (icon-panes id) (foldable? id))) true
    (contains? @showing id)                   (boolean (get @showing id))
    :else                                     (not (boolean (get-in @declared-folds [id :folded])))))

(defn available?
  "Does the island on the screen have this pane at all?"
  [id]
  (contains? @known id))

(defn toggle!
  "Show or hide one pane, and remember it.

   A WRITE THAT CANNOT COMPLETE IS NOT FATAL HERE. localStorage throws in a
   private window and in a thumbnail capture. The pane still toggles for this
   visit, and only the memory of it is lost."
  [id]
  ;; THE EFFECTIVE VALUE AND NOT THE STORED ONE. With no entry yet, `(update …
  ;; not)` reads nil and writes true — which SHOWS a pane the person meant to
  ;; hide, whenever the declaration had it open. showing? knows the answer they
  ;; are looking at; this negates that.
  (let [next (assoc @showing id (not (showing? id)))]
    (reset! showing next)
    (try
      (some-> js/localStorage (.setItem storage-key (js/JSON.stringify (clj->js next))))
      (catch :default _ nil))))

(defn visibility-of-arrangement
  "PURE. What a stored arrangement says about each pane a person may fold:
   `{id true|false}`, or nil when `json` does not parse.

   A REMOVED PANEL IS NOT IN dockview's JSON. A pane the person hid was taken
   out with removePanel, so the `panels` map of the stored arrangement names
   exactly the panes that were on the screen. That is the fact a reload must
   draw again (George, 2026-09-28: \"still reload of a tab does not retain its
   panes state and proportion\").

   `ids` is the set of panes this dock may fold. A pane that cannot fold is
   always drawn, so the arrangement has nothing to say about it."
  [json ids]
  (try
    (when-let [^js parsed (and (string? json) (seq json) (js/JSON.parse json))]
      (when-let [^js panels (.-panels parsed)]
        (let [present (set (js-keys panels))]
          (into {} (for [id ids] [id (contains? present id)])))))
    (catch :default _ nil)))

(defn adopt!
  "Take the visibility a restored arrangement carries, for THIS visit only.

   NOT WRITTEN TO localStorage, and that is deliberate. The stored map is the
   browser's default for a session that has no arrangement yet. The arrangement
   is the session's own record, so it wins for the session it belongs to, and
   another session keeps its own. A person's next toggle writes the whole map,
   as before.

   A value equal to the current one changes nothing, so a restore that is
   applied again does not re-render the dock."
  [m]
  (when (seq m)
    (let [next (merge @showing m)]
      (when (not= next @showing)
        (reset! showing next)))))
